import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MIME_TYPES, randomId } from "@excalidraw/common";
import {
  convertToExcalidrawElements,
  newElementWith,
} from "@excalidraw/element";

import {
  CaptureUpdateAction,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import DialogActionButton from "@excalidraw/excalidraw/components/DialogActionButton";

import type {
  ExcalidrawElement,
  ExcalidrawImageElement,
  FileId,
} from "@excalidraw/element/types";
import type {
  BinaryFileData,
  DataURL,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import "./GraphDialog.scss";

import { getCustomToolShortcutLabel } from "./customToolShortcuts";

const GRAPH_DATA_KEY = "excalimathGraph";
const GRAPH_WIDTH = 640;
const GRAPH_HEIGHT = 400;
const SAMPLE_COUNT = 700;
const COLORS = ["#7048e8", "#e64980", "#1971c2", "#2f9e44", "#f08c00"];

type GraphExpression = {
  id: string;
  expression: string;
  color: string;
  visible: boolean;
};

export type GraphState = {
  version: 1;
  expressions: GraphExpression[];
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  xTickStep?: number;
  yTickStep?: number;
  showGrid: boolean;
};

type PlotlyModule = typeof import("plotly.js-dist-min");

const createDefaultState = (): GraphState => ({
  version: 1,
  expressions: [
    { id: randomId(), expression: "", color: COLORS[0], visible: true },
  ],
  xMin: -10,
  xMax: 10,
  yMin: -10,
  yMax: 10,
  xTickStep: undefined,
  yTickStep: undefined,
  showGrid: true,
});

const copyGraphState = (state: GraphState): GraphState => ({
  ...state,
  expressions: state.expressions.map((expression) => ({ ...expression })),
});

export const getGraphState = (element: ExcalidrawElement | undefined) => {
  const value = element?.customData?.[GRAPH_DATA_KEY];
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as GraphState;
};

const getSelectedGraph = (api: ExcalidrawImperativeAPI) => {
  const selectedIds = api.getAppState().selectedElementIds;
  return api
    .getSceneElements()
    .find((element) => selectedIds[element.id] && getGraphState(element));
};

const makePlotData = async (state: GraphState) => {
  if (!(state.xMax > state.xMin) || !(state.yMax > state.yMin)) {
    throw new Error("Axis maximums must be greater than minimums.");
  }

  const { compile } = await import("mathjs");

  return state.expressions
    .filter((item) => item.visible && item.expression.trim())
    .map((item) => {
      const evaluator = compile(item.expression);
      const x: number[] = [];
      const y: Array<number | null> = [];
      const step = (state.xMax - state.xMin) / (SAMPLE_COUNT - 1);
      let previous: number | null = null;

      for (let index = 0; index < SAMPLE_COUNT; index++) {
        const xValue = state.xMin + step * index;
        let yValue: number | null = null;

        try {
          const result = evaluator.evaluate({ x: xValue });
          if (typeof result === "number" && Number.isFinite(result)) {
            yValue = result;
          }
        } catch {
          yValue = null;
        }

        const jumpLimit = Math.max(20, (state.yMax - state.yMin) * 4);
        if (
          previous !== null &&
          yValue !== null &&
          Math.abs(yValue - previous) > jumpLimit
        ) {
          yValue = null;
        }

        x.push(xValue);
        y.push(yValue);
        previous = yValue;
      }

      return {
        x,
        y,
        type: "scatter" as const,
        mode: "lines" as const,
        name: item.expression,
        connectgaps: false,
        line: { color: item.color, width: 3 },
        hovertemplate: "x=%{x:.3g}<br>y=%{y:.3g}<extra></extra>",
      };
    });
};

const makeLayout = (state: GraphState, darkMode: boolean) => {
  const background = darkMode ? "#121212" : "#ffffff";
  const foreground = darkMode ? "#f1f3f5" : "#1b1b1f";
  const grid = darkMode ? "#34343a" : "#e7e7ec";

  return {
    autosize: true,
    paper_bgcolor: background,
    plot_bgcolor: background,
    font: { color: foreground, family: "Virgil, sans-serif", size: 13 },
    margin: { l: 48, r: 20, t: 24, b: 42 },
    showlegend: state.expressions.filter((item) => item.visible).length > 1,
    legend: { orientation: "h" as const, y: 1.08, x: 0 },
    xaxis: {
      range: [state.xMin, state.xMax],
      dtick: state.xTickStep,
      zeroline: true,
      zerolinecolor: foreground,
      zerolinewidth: 1.5,
      showgrid: state.showGrid,
      gridcolor: grid,
      linecolor: foreground,
      ticks: "outside" as const,
    },
    yaxis: {
      range: [state.yMin, state.yMax],
      dtick: state.yTickStep,
      zeroline: true,
      zerolinecolor: foreground,
      zerolinewidth: 1.5,
      showgrid: state.showGrid,
      gridcolor: grid,
      linecolor: foreground,
      ticks: "outside" as const,
    },
  };
};

const plotConfig = {
  responsive: true,
  displaylogo: false,
  scrollZoom: true,
  modeBarButtonsToRemove: ["lasso2d", "select2d", "autoScale2d"],
};

const loadPlotly = async () => (await import("plotly.js-dist-min")).default;

export const GraphIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path d="M4 3v17h17" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M5.5 16.5c3-1 4.2-6.8 7-6.8 2.4 0 2.7 3.2 4.7 3.2 1.3 0 2.1-1.4 3.1-4.4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const GraphToolbarButton = ({ onClick }: { onClick: () => void }) => (
  <button
    className="GraphToolbarButton"
    type="button"
    onClick={onClick}
    title={`Create or edit a function graph — ${getCustomToolShortcutLabel(
      "graph",
    )}`}
    aria-label="Create or edit a function graph"
    aria-keyshortcuts={getCustomToolShortcutLabel("graph")}
  >
    <GraphIcon />
    <span>Graph</span>
    <kbd>{getCustomToolShortcutLabel("graph")}</kbd>
  </button>
);

export const GraphDialog = ({
  api,
  editingElementId,
  onClose,
}: {
  api: ExcalidrawImperativeAPI;
  editingElementId: string | null;
  onClose: () => void;
}) => {
  const selectedGraph = useMemo(
    () =>
      editingElementId
        ? api
            .getSceneElements()
            .find((element) => element.id === editingElementId)
        : getSelectedGraph(api),
    [api, editingElementId],
  );
  const [state, setState] = useState<GraphState>(() => {
    const saved = getGraphState(selectedGraph);
    return saved ? copyGraphState(saved) : createDefaultState();
  });
  const [error, setError] = useState<string | null>(null);
  const [isInserting, setIsInserting] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const plotlyRef = useRef<PlotlyModule["default"] | null>(null);
  const darkMode = api.getAppState().theme === "dark";

  const renderPreview = useCallback(async () => {
    if (!previewRef.current) {
      return false;
    }

    try {
      if (
        (state.xTickStep !== undefined && state.xTickStep <= 0) ||
        (state.yTickStep !== undefined && state.yTickStep <= 0)
      ) {
        throw new Error("Tick steps must be greater than zero.");
      }
      const plotly = await loadPlotly();
      plotlyRef.current = plotly;
      const data = await makePlotData(state);
      if (!data.length) {
        throw new Error("Add at least one visible expression.");
      }
      await plotly.react(
        previewRef.current,
        data,
        makeLayout(state, darkMode),
        plotConfig,
      );
      setError(null);
      return true;
    } catch (previewError) {
      setError(
        previewError instanceof Error
          ? previewError.message
          : "The graph cannot be rendered.",
      );
      return false;
    }
  }, [darkMode, state]);

  useEffect(() => {
    const ownerWindow = previewRef.current?.ownerDocument.defaultView;
    if (!ownerWindow) {
      return;
    }
    const timeout = ownerWindow.setTimeout(renderPreview, 120);
    return () => ownerWindow.clearTimeout(timeout);
  }, [renderPreview]);

  useEffect(
    () => () => {
      if (plotlyRef.current && previewRef.current) {
        plotlyRef.current.purge(previewRef.current);
      }
    },
    [],
  );

  const updateExpression = (id: string, updates: Partial<GraphExpression>) => {
    setState((current) => ({
      ...current,
      expressions: current.expressions.map((item) =>
        item.id === id ? { ...item, ...updates } : item,
      ),
    }));
  };

  const addExpression = () => {
    if (state.expressions.length >= COLORS.length) {
      return;
    }
    setState((current) => ({
      ...current,
      expressions: [
        ...current.expressions,
        {
          id: randomId(),
          expression: "",
          color: COLORS[current.expressions.length],
          visible: true,
        },
      ],
    }));
  };

  const applyPreset = (expression: string, bounds?: Partial<GraphState>) => {
    setState((current) => ({
      ...current,
      ...bounds,
      expressions: [
        {
          id: randomId(),
          expression,
          color: COLORS[0],
          visible: true,
        },
      ],
    }));
  };

  const insertGraph = async () => {
    if (!previewRef.current || !plotlyRef.current || !(await renderPreview())) {
      return;
    }

    setIsInserting(true);
    try {
      const dataURL = (await plotlyRef.current.toImage(previewRef.current, {
        format: "png",
        width: 1280,
        height: 800,
      })) as DataURL;
      const fileId = randomId() as FileId;
      const now = Date.now();
      const file: BinaryFileData = {
        id: fileId,
        mimeType: MIME_TYPES.png,
        dataURL,
        created: now,
        lastRetrieved: now,
      };
      const graphData = copyGraphState(state);
      api.addFiles([file]);

      if (selectedGraph?.type === "image") {
        const updated = newElementWith(
          selectedGraph as ExcalidrawImageElement,
          {
            fileId,
            status: "pending",
            customData: {
              ...selectedGraph.customData,
              [GRAPH_DATA_KEY]: graphData,
            },
          },
        );
        api.updateScene({
          elements: api
            .getSceneElementsIncludingDeleted()
            .map((element) => (element.id === updated.id ? updated : element)),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      } else {
        const appState = api.getAppState();
        const center = viewportCoordsToSceneCoords(
          {
            clientX: appState.offsetLeft + appState.width / 2,
            clientY: appState.offsetTop + appState.height / 2,
          },
          appState,
        );
        const x = center.x - GRAPH_WIDTH / 2;
        const y = center.y - GRAPH_HEIGHT / 2;
        const [element] = convertToExcalidrawElements(
          [
            {
              type: "image",
              x,
              y,
              width: GRAPH_WIDTH,
              height: GRAPH_HEIGHT,
              fileId,
              status: "pending",
              customData: { [GRAPH_DATA_KEY]: graphData },
            },
          ],
          { regenerateIds: true },
        );

        api.updateScene({
          elements: [...api.getSceneElementsIncludingDeleted(), element],
          appState: { selectedElementIds: { [element.id]: true } },
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      }

      onClose();
    } catch (insertError) {
      setError(
        insertError instanceof Error
          ? insertError.message
          : "The graph cannot be inserted.",
      );
    } finally {
      setIsInserting(false);
    }
  };

  return (
    <Dialog
      title={selectedGraph ? "Edit graph" : "Create graph"}
      onCloseRequest={onClose}
      size="wide"
      className="GraphDialog"
    >
      <div className="GraphDialog__workspace">
        <section className="GraphDialog__controls" aria-label="Graph settings">
          <div>
            <div className="GraphDialog__eyebrow">Functions</div>
            <p className="GraphDialog__hint">
              Use x as the variable. Examples: x^2, sin(x), 2*x + 1.
            </p>
          </div>

          <div className="GraphDialog__expressions">
            {state.expressions.map((item, index) => (
              <div className="GraphDialog__expression" key={item.id}>
                <button
                  type="button"
                  className="GraphDialog__visibility"
                  style={{ color: item.color }}
                  aria-label={item.visible ? "Hide function" : "Show function"}
                  aria-pressed={item.visible}
                  onClick={() =>
                    updateExpression(item.id, { visible: !item.visible })
                  }
                >
                  {item.visible ? "●" : "○"}
                </button>
                <label htmlFor={`graph-expression-${item.id}`}>
                  {index + 1}
                </label>
                <input
                  id={`graph-expression-${item.id}`}
                  value={item.expression}
                  onChange={(event) =>
                    updateExpression(item.id, {
                      expression: event.target.value,
                    })
                  }
                  placeholder="sin(x)"
                  autoComplete="off"
                  spellCheck={false}
                />
                {state.expressions.length > 1 && (
                  <button
                    type="button"
                    className="GraphDialog__remove"
                    aria-label={`Remove function ${index + 1}`}
                    onClick={() =>
                      setState((current) => ({
                        ...current,
                        expressions: current.expressions.filter(
                          (candidate) => candidate.id !== item.id,
                        ),
                      }))
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="GraphDialog__add"
            onClick={addExpression}
            disabled={state.expressions.length >= COLORS.length}
          >
            + Add function
          </button>

          <div>
            <div className="GraphDialog__eyebrow">Examples</div>
            <div className="GraphDialog__presets">
              <button type="button" onClick={() => applyPreset("2*x + 1")}>
                Linear
              </button>
              <button type="button" onClick={() => applyPreset("x^2")}>
                Parabola
              </button>
              <button
                type="button"
                onClick={() =>
                  applyPreset("sin(x)", {
                    xMin: -2 * Math.PI,
                    xMax: 2 * Math.PI,
                    yMin: -2,
                    yMax: 2,
                  })
                }
              >
                Sine
              </button>
              <button type="button" onClick={() => applyPreset("1/x")}>
                Reciprocal
              </button>
              <button type="button" onClick={() => applyPreset("abs(x)")}>
                Absolute value
              </button>
            </div>
          </div>

          <fieldset className="GraphDialog__axes">
            <legend>Axis range</legend>
            {(["xMin", "xMax", "yMin", "yMax"] as const).map((key) => (
              <label key={key}>
                <span>{key.replace("Min", " min").replace("Max", " max")}</span>
                <input
                  type="number"
                  step="any"
                  value={state[key]}
                  onChange={(event) =>
                    setState((current) => ({
                      ...current,
                      [key]: Number(event.target.value),
                    }))
                  }
                />
              </label>
            ))}
          </fieldset>

          <fieldset className="GraphDialog__axes">
            <legend>Tick spacing</legend>
            {(["xTickStep", "yTickStep"] as const).map((key) => (
              <label key={key}>
                <span>{key === "xTickStep" ? "X step" : "Y step"}</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={state[key] ?? ""}
                  placeholder="Auto"
                  onChange={(event) =>
                    setState((current) => ({
                      ...current,
                      [key]: event.target.value
                        ? Number(event.target.value)
                        : undefined,
                    }))
                  }
                />
              </label>
            ))}
          </fieldset>

          <label className="GraphDialog__gridToggle">
            <input
              type="checkbox"
              checked={state.showGrid}
              onChange={(event) =>
                setState((current) => ({
                  ...current,
                  showGrid: event.target.checked,
                }))
              }
            />
            Show grid
          </label>
        </section>

        <section
          className="GraphDialog__previewSection"
          aria-label="Graph preview"
        >
          <div className="GraphDialog__preview" ref={previewRef} />
          {error && <div className="GraphDialog__error">{error}</div>}
          <div className="GraphDialog__footer">
            <span>
              {selectedGraph
                ? "This replaces the selected graph."
                : "The graph will be placed at the center of the board."}
            </span>
            <div className="GraphDialog__actions">
              <DialogActionButton label="Cancel" onClick={onClose} />
              <DialogActionButton
                label={selectedGraph ? "Update graph" : "Insert graph"}
                actionType="primary"
                onClick={insertGraph}
                disabled={Boolean(error) || isInserting}
                isLoading={isInserting}
              >
                <GraphIcon size={18} />
              </DialogActionButton>
            </div>
          </div>
        </section>
      </div>
    </Dialog>
  );
};

export const graphTestUtils = {
  getGraphState,
  makeLayout,
  makePlotData,
};
